import { useState, useEffect } from 'react';
import { useMail } from '../../context/MailContext';
import { format } from 'date-fns';
import './EmailView.css';

const AVATAR_COLORS = {
  teal: { bg: '#E1F5EE', text: '#0F6E56' },
  blue: { bg: '#E6F1FB', text: '#0C447C' },
  amber: { bg: '#FAEEDA', text: '#633806' },
  pink: { bg: '#FBEAF0', text: '#4B1528' },
  purple: { bg: '#EEEDFE', text: '#26215C' },
};

export default function EmailView() {
  const {
    activeEmail, openCompose, deleteEmail, toggleStar,
    undoQueue, recallEmail, cancelScheduled,
  } = useMail();
  const [undoCountdown, setUndoCountdown] = useState(10);
  const [showUndoBar, setShowUndoBar] = useState(false);

  // Track undo queue changes
  useEffect(() => {
    if (undoQueue.length > 0) {
      setShowUndoBar(true);
      setUndoCountdown(10);
      const interval = setInterval(() => {
        setUndoCountdown(c => {
          if (c <= 1) { setShowUndoBar(false); clearInterval(interval); return 0; }
          return c - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setShowUndoBar(false);
    }
  }, [undoQueue]);

  if (!activeEmail) {
    return (
      <main className="email-view empty-view">
        {showUndoBar && (
          <UndoBar
            countdown={undoCountdown}
            emailId={undoQueue[0]}
            onUndo={recallEmail}
            onDismiss={() => setShowUndoBar(false)}
          />
        )}
        <div className="empty-state">
          <div className="empty-icon-large">📧</div>
          <h3>Select an email to read</h3>
          <p>Choose from your inbox on the left</p>
        </div>
      </main>
    );
  }

  const { from, subject, body, date, attachments, readReceipt, readAt, readBy,
          folder, scheduledFor, sentReadBy, sentTo, starred, id } = activeEmail;
  const colors = AVATAR_COLORS[from.color] || AVATAR_COLORS.blue;

  return (
    <main className="email-view">
      {showUndoBar && (
        <UndoBar
          countdown={undoCountdown}
          emailId={undoQueue[0]}
          onUndo={recallEmail}
          onDismiss={() => setShowUndoBar(false)}
        />
      )}

      {readReceipt && readAt && (
        <div className="receipt-bar">
          <span className="receipt-icon">✓</span>
          <span>
            Read receipt: <strong>{readBy}</strong> opened this email ·{' '}
            {format(readAt, 'MMMM d \'at\' h:mm a')}
          </span>
        </div>
      )}

      {folder === 'scheduled' && scheduledFor && (
        <div className="scheduled-bar">
          <span>🕐</span>
          <span>
            Scheduled to send on{' '}
            <strong>{format(scheduledFor, 'EEE, MMM d \'at\' h:mm a')}</strong>
          </span>
          <button
            className="cancel-sched-btn"
            onClick={() => cancelScheduled(id)}
          >
            Cancel &amp; edit
          </button>
        </div>
      )}

      {folder === 'sent' && sentReadBy !== undefined && (
        <div className="sent-receipt-bar">
          <span>📬</span>
          <span>Read by <strong>{sentReadBy} of {sentTo}</strong> recipients</span>
        </div>
      )}

      <div className="ev-header">
        <h1 className="ev-subject">{subject}</h1>

        <div className="ev-meta-row">
          <div
            className="ev-avatar"
            style={{ background: colors.bg, color: colors.text }}
          >
            {from.initials}
          </div>
          <div className="ev-from">
            <strong>{from.name}</strong>
            <span className="ev-email">&lt;{from.email}&gt;</span>
            <span className="ev-date">{date ? format(date, 'MMM d, yyyy · h:mm a') : ''}</span>
          </div>
          <div className="ev-actions">
            <button
              className={`ev-action-btn ${starred ? 'starred' : ''}`}
              onClick={() => toggleStar(id)}
              title={starred ? 'Unstar' : 'Star'}
            >
              {starred ? '⭐' : '☆'}
            </button>
            <button className="ev-action-btn" title="Reply"
              onClick={() => openCompose({ to: from.email, subject: `Re: ${subject}` })}>
              ↩ Reply
            </button>
            <button className="ev-action-btn" title="Forward"
              onClick={() => openCompose({ subject: `Fwd: ${subject}`, body })}>
              ↪ Forward
            </button>
            <button className="ev-action-btn danger" title="Delete"
              onClick={() => deleteEmail(id)}>
              🗑
            </button>
          </div>
        </div>
      </div>

      <div className="ev-body" dangerouslySetInnerHTML={{ __html: body }} />

      {attachments?.length > 0 && (
        <div className="ev-attachments">
          <div className="attach-label">📎 Attachments ({attachments.length})</div>
          <div className="attach-list">
            {attachments.map((a, i) => (
              <div key={i} className="attach-item">
                <span className="attach-icon">
                  {a.type === 'pdf' ? '📄' : a.type === 'xlsx' ? '📊' : '📁'}
                </span>
                <div className="attach-info">
                  <div className="attach-name">{a.name}</div>
                  <div className="attach-size">{a.size}</div>
                </div>
                <button className="attach-dl">⬇ Download</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ev-footer">
        <button className="btn-primary"
          onClick={() => openCompose({ to: from.email, subject: `Re: ${subject}` })}>
          ↩ Reply
        </button>
        <button className="btn-ghost"
          onClick={() => openCompose({ subject: `Fwd: ${subject}`, body })}>
          ↪ Forward
        </button>
        <button className="btn-ghost"
          onClick={() => openCompose()}>
          ✏ New Email
        </button>
      </div>
    </main>
  );
}

function UndoBar({ countdown, emailId, onUndo, onDismiss }) {
  const circumference = 2 * Math.PI * 8;
  const progress = (countdown / 10) * circumference;

  return (
    <div className="undo-bar">
      <svg width="20" height="20" viewBox="0 0 20 20" className="undo-ring">
        <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
        <circle
          cx="10" cy="10" r="8" fill="none"
          stroke="white" strokeWidth="2"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          transform="rotate(-90 10 10)"
        />
        <text x="10" y="14" textAnchor="middle" fill="white" fontSize="8" fontWeight="600">
          {countdown}
        </text>
      </svg>
      <span>Email sent — {countdown}s to recall</span>
      <button className="undo-action-btn" onClick={() => { onUndo(emailId); onDismiss(); }}>
        Undo Send
      </button>
      <button className="undo-dismiss" onClick={onDismiss}>✕</button>
    </div>
  );
}
